import { __ } from '@wordpress/i18n';
import { unparse } from 'papaparse';
import PropTypes from 'prop-types';
import React from 'react';

// Config.
import {
  DATA_SHAPE,
  DATAPOINT_SHAPE,
  FILTERS_SHAPE,
} from '@/config/prop-types';

// Services.
import formatCurrency from '@/services/data/format-currency';
import formatPercentage from '@/services/data/format-percentage';
import getTableTitle from '@/services/filters/get-table-title';
import getValueForYear from '@/services/data/get-value-for-year';
import getYearsFromFilters from '@/services/filters/get-years-from-filters';

const Table = ({
  data,
  disambiguatedFilters,
  filters,
  toplineData,
}) => {
  const { outputType, tab } = filters;
  const formatter = outputType.includes('$') && tab <= 1 ? formatCurrency : formatPercentage;
  const years = getYearsFromFilters(disambiguatedFilters);
  const tableTitle = getTableTitle(disambiguatedFilters);
  const tableSubtitle = filters.tab <= 1 ? disambiguatedFilters.outputType : '';

  // Dynamically construct CSV data.
  const csvData = [[tableTitle]];
  if (tableSubtitle) {
    csvData.push([tableSubtitle]);
  }
  const tableHeaders = data.map(({ label }) => label);
  tableHeaders.unshift(__('Year', 'hst'));
  if (filters.tab <= 1) {
    tableHeaders.push(__('Total National Health Expenditures', 'hst'));
  }
  csvData.push(tableHeaders);
  years.forEach((year) => {
    const row = data.map(({ data: seriesData }) => formatter(getValueForYear(seriesData, year)));
    row.unshift(year);
    if (filters.tab <= 1) {
      row.push(formatter(getValueForYear(toplineData, year)));
    }
    csvData.push(row);
  });
  csvData.push([__('Source: National Health Spending Explorer', 'hst')]);
  csvData.push([__('Peterson-KFF Health System Tracker', 'hst')]);
  csvData.push([__('https://www.healthsystemtracker.org/', 'hst')]);

  return (
    <table className="hst-table">
      <caption>
        <p>
          {tableTitle}
          {tableSubtitle ? (
            <span>{tableSubtitle}</span>
          ) : null}
        </p>
      </caption>
      <thead>
        <tr>
          {tableHeaders.map((label) => (
            <th key={label}>{label}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {years.map((year) => (
          <tr key={year}>
            <td>{year}</td>
            {data.map(({ data: seriesData, label }) => (
              <td key={`${label}-${year}`}>{formatter(getValueForYear(seriesData, year))}</td>
            ))}
            {filters.tab <= 1 ? (
              <td>{formatter(getValueForYear(toplineData, year))}</td>
            ) : null}
          </tr>
        ))}
      </tbody>
      <tfoot>
        <a href={`data:text/csv;charset=utf-8,${encodeURI(unparse(csvData))}`} download="data.csv">
          {__('Download Data', 'hst')}
        </a>
      </tfoot>
    </table>
  );
};

Table.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape(DATA_SHAPE)).isRequired,
  disambiguatedFilters: PropTypes.shape(FILTERS_SHAPE).isRequired,
  filters: PropTypes.shape(FILTERS_SHAPE).isRequired,
  toplineData: PropTypes.arrayOf(PropTypes.shape(DATAPOINT_SHAPE)).isRequired,
};

export default Table;
